import { Alert, Box, Grid } from '@mui/material'
import Button from '@mui/material/Button'
import React from 'react'
import {
  ArrayInput,
  FormDataConsumer,
  FormTab,
  NumberInput,
  SelectInput,
  SimpleFormIterator,
  TextInput,
  minValue,
  required,
} from 'react-admin'
import { PriceInput } from '../../components'
import { validatePlaces } from '../../lib/productPlaces'

const sxStyles = {
  arrayInput: {
    '& p': {
      color: 'grey',
      marginRight: '1em',
      fontWeight: 'bold',
    },
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '1em',
    flexWrap: 'wrap' as const,
  },
}

/**
 * Valor de face que o app antigo consegue ler.
 *
 * Até o build novo chegar às lojas, o app trata `name` como se fosse o valor.
 * Gravar o valor cru (ex.: "10", "10.5") mantém esses apps exibindo certo;
 * o app novo já lê `cashValue` e usa `name` como nome de verdade.
 */
export const legacyNameFor = (cashValue: unknown): string => {
  const numeric = Number(cashValue)
  return Number.isFinite(numeric) ? String(numeric) : ''
}

/** Normaliza o payload para o formato aceito por POST/PATCH /chains/{chainId}/products. */
export const transformMoneyProduct = (data: any) => {
  const payload = { ...data, isCash: true, cashValue: Number(data.cashValue) }

  if (!payload.name || !String(payload.name).trim()) {
    payload.name = legacyNameFor(payload.cashValue)
  }

  if (payload.places) {
    payload.places = payload.places
      .filter((place: any) => place)
      .map(({ placeId, price, partnerPrice }: any) => ({
        placeId,
        price,
        partnerPrice: partnerPrice ? +partnerPrice : null,
      }))
  }

  delete payload.defaultPrice

  return payload
}

interface MoneyProductsFormTabsProps {
  places: Array<{ id: string; name: string }>
}

export const ProductTab = () => (
  <FormTab label="Produto">
    <Grid container spacing={2} sx={{ maxWidth: 800 }}>
      <Grid size={12}>
        <Alert severity="info" sx={{ mb: 1 }}>
          O cliente resgata este produto por pontos e apresenta o cupom no caixa,
          que abate o valor em reais da compra. Deixe o nome em branco para preenchê-lo
          automaticamente com o valor.
        </Alert>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <PriceInput
          source="cashValue"
          label="Valor em reais"
          validate={[required(), minValue(0.01, 'Deve ser maior que zero')]}
          fullWidth
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextInput
          source="name"
          label="Nome"
          helperText="Aparece no app e no cupom. Ex.: Vale 10 reais"
          inputProps={{ maxLength: 50 }}
          InputProps={{ autoComplete: 'off' }}
          fullWidth
        />
      </Grid>
      <Grid size={12}>
        <TextInput
          source="description"
          label="Descrição"
          validate={[required()]}
          defaultValue="Valor em compra"
          InputProps={{ autoComplete: 'off' }}
          fullWidth
        />
      </Grid>
    </Grid>
  </FormTab>
)

export const RedeemTab = ({ places }: MoneyProductsFormTabsProps) => {
  const [addAllPlaces, setAddAllPlaces] = React.useState(false)

  return (
    <FormTab label="Resgate">
      <Box sx={sxStyles.row}>
        <NumberInput
          source="defaultPrice"
          id="defaultPriceInput"
          label="Valor padrão em pontos"
          min={1}
          validate={minValue(1)}
          InputProps={{ autoComplete: 'off' }}
          sx={{ width: 200 }}
        />
        <FormDataConsumer>
          {({ formData }) => {
            const disabled = formData.places ? formData.places.length === places.length : false
            return (
              <Button
                color="primary"
                disabled={disabled || !formData.defaultPrice}
                onClick={() => setAddAllPlaces(true)}
              >
                Adicionar Restantes
              </Button>
            )
          }}
        </FormDataConsumer>
      </Box>

      <FormDataConsumer>
        {({ formData }) => {
          if (addAllPlaces) {
            const current = (formData.places || []).filter((p: any) => p)
            const insertedIds = current.map((p: any) => p.placeId)
            const bulk = places
              .filter(({ id }) => !insertedIds.includes(id))
              .map(({ id, name }) => ({ placeId: id, price: formData.defaultPrice, name }))

            formData.places = current.concat(bulk)
            setAddAllPlaces(false)
            document.getElementById('defaultPriceInput')?.focus()
          }

          return (
            <FormDataConsumer>
              {({ formData: upperFormData, ...upperRest }) => {
                const disableRemove = upperFormData.places ? upperFormData.places.length === 1 : false
                const disableAdd = upperFormData.places
                  ? upperFormData.places.length === places.length
                  : false

                return (
                  <ArrayInput
                    validate={validatePlaces}
                    label=""
                    source="places"
                    sx={sxStyles.arrayInput}
                  >
                    <SimpleFormIterator
                      disableReordering
                      disableRemove={disableRemove}
                      disableAdd={disableAdd}
                      fullWidth
                      {...upperRest}
                    >
                      <FormDataConsumer>
                        {({ formData: innerFormData, scopedFormData }) => {
                          const selectedIds = innerFormData.places
                            ? innerFormData.places.filter((p: any) => p).map((p: any) => p.placeId)
                            : []
                          const availablePlaces = places.filter(
                            ({ id }) =>
                              !selectedIds.includes(id) ||
                              (scopedFormData ? scopedFormData.placeId === id : false),
                          )

                          return (
                            <Box sx={sxStyles.row}>
                              <SelectInput
                                source="placeId"
                                choices={availablePlaces}
                                label="Ponto de troca"
                                emptyText="ra.action.clear_input_value"
                                validate={[required()]}
                                sx={{ width: 240 }}
                              />
                              <NumberInput
                                source="price"
                                label="Valor em pontos"
                                min={1}
                                validate={[required(), minValue(1)]}
                                sx={{ width: 170 }}
                              />
                              <PriceInput
                                source="partnerPrice"
                                label="Valor em reais"
                                helperText="Em branco usa o valor do produto"
                                sx={{ width: 170 }}
                              />
                            </Box>
                          )
                        }}
                      </FormDataConsumer>
                    </SimpleFormIterator>
                  </ArrayInput>
                )
              }}
            </FormDataConsumer>
          )
        }}
      </FormDataConsumer>
    </FormTab>
  )
}
